import { apiErrorMessage } from '@agos/api-client'
import { tt } from '@agos/shared'

/**
 * Who may store a settings section - the module's one shared rule.
 *
 * Every settings controller in the backend reads with `@RequireAuth({"main_admin", "admin"})`, and until
 * `BACKEND.md` §10A.413 nearly every one wrote with `@RequireAuth("main_admin")`. FB-001's resolution was to
 * let in whoever the server lets read and disable what it will not let them store, saying which.
 *
 * **§10A.413 (owner, folded into O119, FB-1076) moved most of those writes onto the right the screen is about**:
 * `@RequirePermission(K)` with the write key alone, which the main administrator passes before anything is read
 * and anybody else passes with a tick. So the question here is the server's again, section by section:
 *
 * | Section | Write |
 * |---|---|
 * | `general` (`/settings/general`, `/system`, the General page's `/basic` keys) | `settings_general` |
 * | `custom-code` (`customHeaderCode`, `customFooterCode` on `/settings/basic`) | **`main_admin`** - it runs on the customer site |
 * | `email` (sender, templates, test e-mail, e-mail wording) | `settings_email` |
 * | `sms`, `sms-loyalty` (`pointsSmsEnabled`, `pointsSmsTemplate`), `sms-contacts` | `settings_sms` |
 * | `notifications`, `pushup` (channels, push wording) | `settings_notifications_edit` |
 * | `monitoring` (`POST /health-monitor/test`) | `settings_monitoring` |
 * | `ai-translate` (`/settings/basic`'s translation keys) | `translations_admin` |
 * | `vat-categories`, `surcharges` (and `PUT /settings/fiskal-shared`) | `fiskal_settings` |
 * | `payments` (the stale-payment sweep; the four checkout preferences under the same right are on *Nastavenia rozvozu*, §9.766) | `online_payments` |
 * | `billing`, `franchise`, `integrations` | **`main_admin`** - the IBAN on every invoice and the approval threshold, the franchise's terms, the integration secrets |
 * | `hikvision`, `backups`, `apk`, `notifications-inbox`, `translations`, `sales-channels` | `main_admin`, `admin` |
 *
 * A section in neither list below is the main administrator's, which is how `billing`, `franchise`, `integrations`
 * and `custom-code` stay theirs: there is no entry to widen by accident, and `write-access.test.ts` says so by name.
 */

export type SystemRole = 'main_admin' | 'admin' | 'cook' | 'waiter' | 'driver'

/**
 * The sections an `admin` may write as well as read, by role rather than by a right.
 *
 * Each entry is checked against the controller, not assumed. `hikvision` (`/hikvision/config`, `/test`,
 * `/sync`), `apk` (`/apk/upload-url`, `/confirm`, `/build`) and `backups` (`/backups/trigger`) are all
 * `@RequireAuth({"main_admin", "admin"})`.
 *
 * **`notifications` was on this list and should not have been.** Every rule-changing endpoint on
 * `NotificationsController` was `main_admin`, and §10A.413 put them on `settings_notifications_edit`. What an
 * `admin` may do by role is mark their own inbox read (`POST /notifications/{id}/read` and `/read-all`), which is
 * a different section and is listed as one.
 *
 * **`vat-categories` was on this list and should not have been either - FB-1076.** `POST`, `PATCH` and `DELETE
 * /vat-categories` had been `main_admin` since `a4ca0749`, so the VAT screen offered an administrator category and
 * rate writes the server refused. They ask `fiskal_settings` now (§10A.413) and the section is in
 * `PERMISSION_WRITABLE`. A channel's VAT slot on the same screen is `PATCH /sales-channels/{id}`, which is
 * `sales-channels` here.
 */
export const ADMIN_WRITABLE: readonly string[] = [
  'hikvision',
  'backups',
  'apk',
  'notifications-inbox',
  // `PATCH`/`DELETE /translations/{id}` and `POST /translations/bulk-delete` are all
  // `@RequireAuth({"main_admin", "admin"})` - the translation memory is not a settings blob and does not
  // follow the module's read/write split. The three bulk translation runs are the same role list.
  'translations',
  // §9.5's channel editor. `POST`/`PATCH`/`DELETE /sales-channels` are `{main_admin, admin}`; the `GET`
  // admits every role, because every screen that renders an order has to know what its channel does.
  'sales-channels',
]

/**
 * The sections whose write is gated on a **right** rather than on a role.
 *
 * `@RequirePermission("K")` - anybody holding the flag may write, whatever their role, and a main admin may anyway
 * (`EmployeePermissions.isAllowed` passes them before reading anything, as `hasPermission` does here). A role check
 * alone would lock out exactly the person the flag was created for.
 */
export const PERMISSION_WRITABLE: Readonly<Record<string, string>> = {
  'opening-hours': 'opening_hours_edit',
  // §9.699, FB-1005 - `GET`/`POST /settings/sms` and its test send are `{main_admin, admin}` plus `settings_sms`,
  // the route's own gate.
  sms: 'settings_sms',
  // §9.758, BACKEND.md §10A.413 - the rest of each settings screen's writes, on the right the screen is about.
  // The points SMS saves through `PUT /loyalty/settings`, which asks by field: `pointsSmsEnabled` and
  // `pointsSmsTemplate` are `settings_sms`'s. The contact book's delete is `settings_sms` and its add and edit are
  // `{main_admin, admin}`, which everybody the route admits already is.
  'sms-loyalty': 'settings_sms',
  'sms-contacts': 'settings_sms',
  email: 'settings_email',
  notifications: 'settings_notifications_edit',
  pushup: 'settings_notifications_edit',
  monitoring: 'settings_monitoring',
  general: 'settings_general',
  'ai-translate': 'translations_admin',
  'vat-categories': 'fiskal_settings',
  surcharges: 'fiskal_settings',
  payments: 'online_payments',
}

/**
 * Whether this person may store `section`.
 *
 * **`hasPermission` is required** - §9.480's rule: an optional reader is dropped at a call site with nothing to
 * say so, and then a right-gated section quietly answers *no* for everybody but the main administrator, which is
 * exactly what every screen here did before §10A.413. The session's own `useAuth().hasPermission`, which is true
 * for the main administrator and otherwise reads the person's rights.
 */
export function mayWrite(
  section: string,
  role: string | undefined,
  hasPermission: (permission: string) => boolean,
): boolean {
  if (role === 'main_admin') return true
  const flag = PERMISSION_WRITABLE[section]
  if (flag !== undefined) return hasPermission(flag)
  if (role === 'admin') return ADMIN_WRITABLE.includes(section)
  return false
}

/** Said on the page, before anything is typed into a field that cannot be stored. */
export function readOnlyReason(
  section: string,
  role: string | undefined,
  hasPermission: (permission: string) => boolean,
): string | null {
  if (mayWrite(section, role, hasPermission)) return null
  if (PERMISSION_WRITABLE[section] !== undefined) {
    return tt('Na zmenu týchto nastavení potrebujete príslušné právo. Vidieť ich môžete, uložiť nie.')
  }
  if (role === 'admin') {
    return tt(
      'Tieto nastavenia môže uložiť len hlavný administrátor. Vidieť ich môžete, uložiť nie - server zmenu odmietne.',
    )
  }
  return tt('Na uloženie týchto nastavení nemáte právo.')
}

/**
 * Whether a response actually stored anything.
 *
 * **FB-130.** `fetch` resolves for a 403 as happily as for a 200, so legacy's
 *
 * ```js
 * await Promise.all([fetch(…general), fetch(…system), fetch(…basic)])
 * setSaved(true)
 * ```
 *
 * shows the green tick after three refusals. There is no `res.ok` anywhere on the page, and the `catch`
 * only fires on a network error — which a 403 is not. For an `admin`, who may read every one of those
 * sections and write none of them, the tick is *always* false.
 *
 * The generated client throws on a non-2xx, so a mutation's `onError` is the honest place. What this
 * function exists for is the case legacy could not express at all: three independent writes, some of
 * which succeeded.
 */
export interface SaveOutcome {
  /** The sections that stored. */
  saved: readonly string[]
  /** The sections that refused. */
  failed: readonly string[]
  /**
   * What the refusals actually said - §9.392, FB-697.
   *
   * Distinct, because three writes refused by one guard carry one sentence three times and a toast
   * repeating it is noise. Empty when no refusal carried a message, which is a real case: a 403
   * with no body says nothing and the screen has to say so itself.
   */
  reasons: readonly string[]
  state: 'all' | 'partial' | 'none'
}

/**
 * One settled write, as `saveOutcome` wants it - §9.392.
 *
 * Here rather than at the call site so the three lines that use it cannot disagree, and typed off
 * `PromiseSettledResult` so a `fulfilled` result cannot be read for a reason it does not have.
 */
export function settled(result: PromiseSettledResult<unknown> | undefined): {
  ok: boolean
  reason?: unknown
} {
  if (result === undefined) return { ok: false }
  return result.status === 'fulfilled' ? { ok: true } : { ok: false, reason: result.reason }
}

export function saveOutcome(
  results: { section: string; ok: boolean; reason?: unknown }[],
): SaveOutcome {
  const saved = results.filter((result) => result.ok).map((result) => result.section)
  const failed = results.filter((result) => !result.ok).map((result) => result.section)
  // §9.392 - `Promise.allSettled` hands every rejection's `reason` to the caller and this used to
  // reduce it to a boolean, so the server's own words were gone before a message was composed.
  const said = results
    .filter((result) => !result.ok && result.reason !== undefined)
    .map((result) => apiErrorMessage(result.reason, ''))
    .filter((sentence) => sentence !== '')
  return {
    saved,
    failed,
    reasons: [...new Set(said)],
    state: failed.length === 0 ? 'all' : saved.length === 0 ? 'none' : 'partial',
  }
}

/**
 * What to tell somebody after a save that half worked.
 *
 * Legacy has one message for three requests, so a half-written settings page reads as a saved one. A
 * partial save is the state worth naming: the screen and the server now disagree about *some* of it, and
 * which some matters.
 */
export function saveMessage(outcome: SaveOutcome): { title: string; description?: string } {
  if (outcome.state === 'all') return { title: tt('Nastavenia uložené') }
  // §9.392 - the server's sentence where there is one. `Na serveri sa nič nezmenilo` stays for a
  // refusal with an empty body, because that case still has to be named.
  const said = outcome.reasons.join(' ')
  if (outcome.state === 'none') {
    return {
      title: tt('Uloženie zlyhalo'),
      description: said === '' ? tt('Na serveri sa nič nezmenilo.') : said,
    }
  }
  const which = `${tt('Neuložené')}: ${outcome.failed.join(', ')}. ${tt('Obnovte stránku, aby ste videli, čo na serveri naozaj je.')}`
  return {
    title: tt('Uložila sa len časť nastavení'),
    // Appended rather than substituted: *which* half failed is the fact that sends somebody to
    // reload the page, and the reason is why they cannot simply save again.
    description: said === '' ? which : `${which} ${said}`,
  }
}
